import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ExtranetService } from './extranet.service';
import { ViewAsService } from './view-as.service';
import { PropertiesService } from './properties.service';
import { AccessLevel, AreaPermissions, ExtranetProfile, ExtranetRole, MeResponse, PageArea } from '../models/extranet.model';
import { isTeamManager, levelOf } from '../util/access-level';

/** Real, BE-resolved caller identity (GET /extranet/access/me) — same source hotel-manager.guard.ts
 *  already uses, just shared here so components don't each need their own call. */
@Injectable({ providedIn: 'root' })
export class IdentityService {
  private extranetSvc = inject(ExtranetService);
  private router = inject(Router);
  private viewAs = inject(ViewAsService);
  private propSvc = inject(PropertiesService);

  /** The last GET /me answer. Since Feature 4123 the area guards write every navigation's answer
   *  here (so the menu follows a permission change): an answer equal to the current one is not a
   *  change, so the screens whose effects read `me()` do not reload on every navigation. */
  readonly me = signal<MeResponse | null>(null, { equal: sameIdentity });

  // MeResponse has no display name — derive one from the email local-part (e.g. "marco.rossi" -> "Marco Rossi").
  readonly displayName = computed(() => {
    const email = this.me()?.email;
    if (!email) return '';
    return email.split('@')[0]
      .split(/[._-]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  });

  readonly firstName = computed(() => this.displayName().split(' ')[0] ?? '');

  // The account's active role and its full entitlement set. `canSwitchRole` gates the dev-only
  // "View as" control: the BE only ever returns >1 role when its switcher is enabled (dev/local),
  // so on staging/production this is always false and the control never renders.
  readonly activeRole = computed<ExtranetRole | null>(() => this.me()?.role ?? null);
  readonly availableRoles = computed<ExtranetRole[]>(() => this.me()?.availableRoles ?? []);
  readonly canSwitchRole = computed(() => this.availableRoles().length > 1);

  /** True while a platform admin is viewing the product as a group (F7): `me` then describes the
   *  viewed group's manager. Writes are held back in the browser until the admin switches on
   *  write mode from the banner (F9, `ViewAsService.writeMode`); the BE itself runs any request
   *  under the pin as the group. */
  readonly isViewingAs = computed(() => !!this.me()?.viewAsGroupId);

  // ---- Profiles and page permissions (Feature 4123) ---------------------------------------------
  // The member's profiles and effective level per page area, straight from GET /me. The BE
  // enforces them on every request; here they decide what the shell and the pages offer.

  /** The member's profiles; `[OWNER]` under view-as and for a legacy hotel account; empty for a
   *  platform admin outside view-as and while the identity is not resolved. */
  readonly profiles = computed<ExtranetProfile[]>(() => this.me()?.profiles ?? []);
  /** The effective level per area of the group's type (the highest among the profiles). */
  readonly permissions = computed<AreaPermissions>(() => this.me()?.permissions ?? {});
  /** Holds the OWNER profile. */
  readonly isOwner = computed(() => this.profiles().includes('OWNER'));
  /** The Owner of a group: invites members and changes their profiles — the one test of the Team
   *  tab, the account menu's "Invite a teammate" and `ownerGuard` (see `isTeamManager`). */
  readonly canManageTeam = computed(() => isTeamManager(this.me()));

  /** The level on an area — a platform admin outside view-as is not restricted (EDIT); an area
   *  missing from the map is NONE; nothing is granted before the identity is resolved (see
   *  `levelOf`). Reads the `me` signal, so it is reactive inside a computed, an effect or a template. */
  levelOn(area: PageArea): AccessLevel {
    return levelOf(this.me(), area);
  }

  /** READ or EDIT on the area: its pages exist for the member. */
  canRead(area: PageArea): boolean {
    return this.levelOn(area) !== 'NONE';
  }

  /** EDIT on the area: its create / change / delete actions are offered. */
  canEdit(area: PageArea): boolean {
    return this.levelOn(area) === 'EDIT';
  }

  /** Earliest time (ms) the next `refreshPermissions()` may call the BE. */
  private nextPermissionRefreshAt = 0;

  /** Re-resolves the identity after the BE refused a request on the area permissions (the
   *  member's profiles or the admin matrix may have changed since GET /me last answered), so the
   *  menu and the pages follow. At most once per 10 s: a screen firing several refused requests
   *  at once triggers one GET /me. */
  refreshPermissions(): void {
    const now = Date.now();
    if (now < this.nextPermissionRefreshAt) return;
    this.nextPermissionRefreshAt = now + IdentityService.PERMISSION_REFRESH_MS;
    this.load();
  }

  private static readonly PERMISSION_REFRESH_MS = 10_000;

  /** Resolve (or re-resolve) the identity; `onDone` runs once the answer is in. */
  load(onDone?: (me: MeResponse) => void) {
    this.extranetSvc.getMe().subscribe({
      next: (me) => { this.me.set(me); onDone?.(me); },
      error: (err) => console.error('[IdentityService] getMe failed:', err),
    });
  }

  /** Dev-only "View as": persist the picked role, re-resolve identity with it, then land on the
   *  new role's home. Guarded so it can only ever pick a role the account actually holds.
   *
   *  Changing role by hand ends an admin's "open as this group" viewpoint first (AC-17.6): the
   *  pin — and with it F9's write mode — is dropped before the reload, so the banner can never
   *  outlive the viewpoint it describes — even when the picked role equals the impersonated one
   *  (the identity still changes: the admin's own membership replaces the viewed group). */
  switchRole(role: ExtranetRole) {
    if (!this.availableRoles().includes(role)) return;
    const wasViewingAs = this.viewAs.clear();
    if (!wasViewingAs && role === this.activeRole()) return;
    try { sessionStorage.setItem(ExtranetService.ACTIVE_ROLE_KEY, role); } catch { /* private mode */ }
    this.extranetSvc.getMe().subscribe({
      next: (me) => {
        this.me.set(me);
        // The topbar's property list belonged to the viewed group: refresh it for the real one.
        if (wasViewingAs) this.propSvc.load();
        this.router.navigateByUrl(me.role === 'ADMIN' ? '/extranet/admin' : '/extranet/dashboard');
      },
      error: (err) => console.error('[IdentityService] switchRole getMe failed:', err),
    });
  }
}

/** Same identity: the same GET /me answer, field by field (the BE serialises it in a fixed order). */
function sameIdentity(a: MeResponse | null, b: MeResponse | null): boolean {
  return a === b || (a !== null && b !== null && JSON.stringify(a) === JSON.stringify(b));
}

